/**
 * Redteam Constants
 * Adapted from promptfoo/redteam constants for EarlyCore demo flow
 */

import type { FoundationModelId, HttpMethod } from '@/types/redteam';

/**
 * Foundation model providers available for "Our AI" option
 * These match the promptfoo provider format
 */
export interface FoundationModelOption {
  value: FoundationModelId;
  label: string;
  description?: string;
}

export const FOUNDATION_MODELS: FoundationModelOption[] = [
  // OpenAI Models (Platform Managed)
  {
    value: 'openai:gpt-5',
    label: 'OpenAI GPT-5',
    description: 'OpenAI GPT-5 - latest generation model',
  },
  {
    value: 'openai:gpt-5-mini',
    label: 'OpenAI GPT-5 Mini',
    description: 'OpenAI GPT-5 Mini - faster and more cost-effective',
  },
  // Google Models (Platform Managed)
  {
    value: 'google:gemini-2.5-pro',
    label: 'Google Gemini 2.5 Pro',
    description: 'Google Gemini 2.5 Pro',
  },
  {
    value: 'google:gemini-2.5-flash',
    label: 'Google Gemini 2.5 Flash',
    description: 'Google Gemini 2.5 Flash - faster and more cost-effective',
  },
  {
    value: 'google:gemini-3-pro-preview',
    label: 'Google Gemini 3',
    description: 'Google Gemini 3 - latest generation model',
  },
  // Anthropic Models (Requires API Key)
  {
    value: 'anthropic:messages:claude-opus-4-5-20251101',
    label: 'Anthropic Claude 4.5 Opus (Requires API Key)',
    description: 'Anthropic Claude 4.5 Opus - requires your own API key',
  },
  {
    value: 'anthropic:messages:claude-sonnet-4-5-20250929',
    label: 'Anthropic Claude 4.5 Sonnet (Requires API Key)',
    description: 'Anthropic Claude 4.5 Sonnet - requires your own API key',
  },
];

/**
 * Supported HTTP methods for own agent configuration
 */
export const HTTP_METHODS: HttpMethod[] = ['GET', 'POST'];

/**
 * Default configuration values
 */
export const REDTEAM_DEFAULTS = {
  MAX_CONCURRENCY: 4,
  NUM_TESTS: 3,
  DEFAULT_METHOD: 'POST' as HttpMethod,
} as const;

/**
 * Helper to check if a provider ID is a foundation model
 */
export function isFoundationModelProvider(providerId: string | undefined): boolean {
  if (!providerId) {
    return false;
  }

  const foundationPrefixes = ['openai:', 'anthropic:', 'google:', 'vertex:', 'azure:'];
  return foundationPrefixes.some((prefix) => providerId.startsWith(prefix));
}
